import React, { useEffect, useState } from "react";
import { fetchData } from "../../../utils/functions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PaginationDropdown } from "../../../components/common/GeneralComponent";
import { Pagination } from "@mui/material";
import DataTable from "react-data-table-component";
import { Info } from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../../components/partials/Loader";
import GeneralFooter from "../../common/GeneralFooter";

const MPIStandard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];

  const [mpiStandards, setMpiStandards] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [isModalVisible, setModalVisible] = useState(false);
  const [loader, setLoader] = useState(true);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 15,
    totalItems: 0,
    totalPages: 0,
  });

  // Fetch MPI Standards
  const fetchMPIStandards = async () => {
    try {
      const response = await fetchData(
        "mpi-standards",
        "GET",
        null,
        {},
        {
          search: search || undefined,
          page: pagination.currentPage,
          limit: pagination.pageSize,
        },
        navigate,
        "/mpi-standards",
        dispatch
      );
      setMpiStandards(response?.data || []);
      setPagination(response?.pagination || {});
    } catch (error) {
      console.error("Error fetching MPI Standards:", error);
      toast.error("Failed to fetch MPI Standards");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchMPIStandards();
  }, [pagination.currentPage, pagination.pageSize, search]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };

  const showDetailsModal = (row) => {
    setSelectedIndicator(row);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setSelectedIndicator(null);
  };

  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        backgroundColor: "",
        color: "black",
        fontWeight: "bold",
        fontSize: "1.075rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
      },
    },
  };

  const columns = [
    {
      name: <span className="text-[0.813rem]">Sr.No</span>,
      cell: (row, index) => (
        <span>
          {(pagination.currentPage - 1) * pagination.pageSize + index + 1}
        </span>
      ),
      width: "80px",
      sortable: false,
    },
    {
      name: <span className="text-[0.813rem]">MPI Indicator</span>,
      selector: (row) => row.mpi_indicator || "-",
      width: "280px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Weight (%)</span>,
      selector: (row) => row.weight ? `${row.weight}%` : "-",
      width: "120px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Description</span>,
      cell: (row) => (
        <div className="truncate max-w-[400px]">
          {row.description?.substring(0, 100)}...
        </div>
      ),
      sortable: false,
    },
    {
      name: <span className="text-[0.813rem]">Action</span>,
      cell: (row) => (
        <button
          onClick={() => showDetailsModal(row)}
          className="p-2 rounded-full bg-[#137e81] hover:bg-[#0f5f5f] text-white"
          title="View Details"
        >
          <Info size={18} />
        </button>
      ),
      width: "100px",
    },
  ];

  return (
    <div>
      {loader && <Loader />}
      <div className="my-4 w-full flex flex-col justify-between space-y-3">
        <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
          <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
            MPI Standard Indicators
          </h1>
        </div>

        <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
          <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
            <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
              MPI Standard Indicators (15 Indicators)
            </h1>
          </div>
          <div className="p-[1.25rem] w-full">
            <div className="flex lg:flex-row flex-col lg:justify-between justify-start gap-2 lg:items-center items-start mb-4 w-full">
              <PaginationDropdown
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                options={[
                  { value: 15, label: "15" },
                  { value: 30, label: "30" },
                  { value: 50, label: "50" },
                ]}
              />
              <input
                onChange={handleSearchChange}
                placeholder="Search indicators..."
                type="search"
                className="text-[Light] flex items-center bg-[#ebeef2] border-[#ebeef2] rounded-md px-3 py-1 outline-none"
              />
            </div>

            <DataTable
              columns={columns}
              persistTableHead
              data={mpiStandards}
              customStyles={customStyles}
              highlightOnHover
            />
            <GeneralFooter
              pagination={pagination}
              handlePageChange={handlePageChange}
              handlePageSizeChange={handlePageSizeChange}
              dataLength={mpiStandards.length}
            />
          </div>
        </div>

        {/* Details Modal */}
        {isModalVisible && selectedIndicator && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120]">
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-[#137e81]">
                  {selectedIndicator.mpi_indicator}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Weight:</strong> {selectedIndicator.weight}%
                </p>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-2">Description:</h3>
                <div className="text-sm text-gray-700 whitespace-pre-line">
                  {selectedIndicator.description}
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={closeModal}
                  className="px-6 py-2 bg-[#137e81] text-white rounded hover:bg-[#0f5f5f]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MPIStandard;